import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link2, Plus, Trash2, User as UserIcon, X } from 'lucide-react';
import { apiClient } from '../../../lib/api';
import { getErrorDetail } from '../../../utils/errorUtils';
import { AdminOrganization, AdminUserWorkspaceMapping } from '../../../types/admin';
import { useToastContext } from '../../../contexts/ToastContext';
import { Button, ConfirmationModal, Spinner } from '../../ui';
import { CustomSelect } from '../../common/CustomSelect';

/**
 * Admin management of user -> workspace assignment.
 *
 * Mapping a user to a workspace here also switches their active workspace
 * (user_profiles.org_id) — it changes what they actually see in the app, not
 * just a membership grant. Useful for support/onboarding fixes outside the
 * self-serve invite flow.
 */

interface UserSearchResult {
  user_id: string;
  email: string | null;
  display_name: string | null;
}

const ROLE_OPTIONS = [
  { value: 'member', label: 'Member' },
  { value: 'owner', label: 'Owner' },
];


export const UserWorkspaceMappingTab: React.FC = () => {
  const { showToast } = useToastContext();

  const [mappings, setMappings] = useState<AdminUserWorkspaceMapping[]>([]);
  const [orgs, setOrgs] = useState<AdminOrganization[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<AdminUserWorkspaceMapping | null>(null);

  const [selectedUser, setSelectedUser] = useState<UserSearchResult | null>(null);
  const [userQuery, setUserQuery] = useState('');
  const [userResults, setUserResults] = useState<UserSearchResult[]>([]);
  const [userSearchOpen, setUserSearchOpen] = useState(false);
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchReqId = useRef(0);

  const [workspaceId, setWorkspaceId] = useState('');
  const [role, setRole] = useState<'owner' | 'member'>('member');

  const [filterUser, setFilterUser] = useState('');
  const [filterWorkspaceId, setFilterWorkspaceId] = useState('');

  const orgNameById = useMemo(
    () => new Map(orgs.map((o) => [o.id, o.name || o.domain || o.id])),
    [orgs]
  );

  const orgOptions = useMemo(
    () =>
      orgs
        .map((o) => ({
          value: o.id,
          label: o.name ? (o.domain ? `${o.name} (${o.domain})` : o.name) : o.id,
        }))
        .sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase())),
    [orgs]
  );

  const filterWorkspaceOptions = useMemo(
    () => [{ value: '', label: 'All workspaces' }, ...orgOptions],
    [orgOptions]
  );

  const filteredMappings = useMemo(() => {
    const query = filterUser.trim().toLowerCase();
    return mappings.filter((m) => {
      if (filterWorkspaceId && m.workspace_id !== filterWorkspaceId) return false;
      if (!query) return true;
      const haystack = `${m.user_email || ''} ${m.user_name || ''} ${m.user_id}`.toLowerCase();
      return haystack.includes(query);
    });
  }, [mappings, filterUser, filterWorkspaceId]);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const [maps, organizations] = await Promise.all([
        apiClient.listAdminUserWorkspaceMappings(),
        apiClient.getAdminOrganizations(),
      ]);
      setMappings(maps);
      setOrgs(organizations);
    } catch (error) {
      console.error('Failed to load user workspace mappings:', error);
      showToast('Failed to load workspace mappings', 'error');
    } finally {
      setIsLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    const trimmed = userQuery.trim();
    if (trimmed.length < 2) {
      setUserResults([]);
      return;
    }
    searchTimer.current = setTimeout(async () => {
      const reqId = ++searchReqId.current;
      setUserSearchLoading(true);
      try {
        const result = await apiClient.getAdminUsersList(0, 10, trimmed);
        if (reqId !== searchReqId.current) return;
        setUserResults(result.users ?? []);
      } catch (error) {
        console.error('Failed to search users:', error);
      } finally {
        if (reqId === searchReqId.current) setUserSearchLoading(false);
      }
    }, 300);
    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    };
  }, [userQuery]);

  const handlePickUser = (u: UserSearchResult) => {
    setSelectedUser(u);
    setUserQuery('');
    setUserResults([]);
    setUserSearchOpen(false);
  };

  const handleAdd = async () => {
    if (!selectedUser || !workspaceId) return;
    setIsSaving(true);
    try {
      const saved = await apiClient.upsertAdminUserWorkspaceMapping({
        user_id: selectedUser.user_id,
        workspace_id: workspaceId,
        role,
      });
      // switch_active_workspace drops every other membership row for this user server-side
      // (v1 is single-active-workspace) — drop all their prior rows here too, not just the
      // exact (user_id, workspace_id) pair, or a re-mapped user's stale old-workspace row
      // lingers in the table until a manual reload.
      setMappings((prev) => [saved, ...prev.filter((m) => m.user_id !== saved.user_id)]);
      setSelectedUser(null);
      setWorkspaceId('');
      setRole('member');
      showToast(`${saved.user_email || 'User'} mapped successfully`, 'success');
    } catch (error) {
      console.error('Failed to map user to workspace:', error);
      showToast(getErrorDetail(error, 'Failed to map user to workspace'), 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;
    const target = pendingDelete;
    setPendingDelete(null);
    try {
      await apiClient.deleteAdminUserWorkspaceMapping(target.workspace_id, target.user_id);
      setMappings((prev) =>
        prev.filter((m) => !(m.user_id === target.user_id && m.workspace_id === target.workspace_id))
      );
      showToast('Mapping removed', 'success');
    } catch (error) {
      console.error('Failed to delete workspace mapping:', error);
      showToast(getErrorDetail(error, 'Failed to remove mapping'), 'error');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Spinner size="md" text="Loading workspace mappings..." center />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
      {/* Add mapping */}
      <section
        id="user-workspace-add-mapping"
        className="bg-white border border-neutral-200 rounded-lg shadow-card p-5"
      >
        <div className="flex items-center gap-2 mb-1">
          <Link2 className="w-4 h-4 text-primary-600" aria-hidden />
          <h2 className="text-13 font-semibold text-neutral-900">Assign a user to a workspace</h2>
        </div>
        <p className="text-xs text-neutral-500 mb-4">
          Search for a user and pick the client workspace they should belong to. This also
          switches their active workspace — it changes what they see in the app, not just a
          membership grant.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
          <div className="relative sm:col-span-2">
            <label
              htmlFor="user-workspace-user-search"
              className="block text-sm font-medium text-neutral-700 mb-1"
            >
              User
            </label>
            {selectedUser ? (
              <div className="flex items-center justify-between h-10 px-3 border border-neutral-200 rounded-md bg-neutral-50 text-sm text-neutral-900">
                <span className="truncate">{selectedUser.email || selectedUser.user_id}</span>
                <button
                  type="button"
                  onClick={() => setSelectedUser(null)}
                  aria-label="Clear selected user"
                  className="text-neutral-400 hover:text-neutral-600"
                >
                  <X className="w-4 h-4" aria-hidden />
                </button>
              </div>
            ) : (
              <>
                <input
                  id="user-workspace-user-search"
                  type="text"
                  value={userQuery}
                  onChange={(e) => {
                    setUserQuery(e.target.value);
                    setUserSearchOpen(true);
                  }}
                  onFocus={() => setUserSearchOpen(true)}
                  onBlur={() => setTimeout(() => setUserSearchOpen(false), 150)}
                  placeholder="Search by email or name..."
                  autoComplete="off"
                  className="w-full h-10 px-3 border border-neutral-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-600 focus:ring-opacity-10"
                />
                {userSearchOpen && userQuery.trim().length >= 2 && (
                  <div className="absolute z-10 mt-1 w-full bg-white border border-neutral-200 rounded-md shadow-modal max-h-64 overflow-y-auto">
                    {userSearchLoading ? (
                      <div className="px-3 py-2 text-xs text-neutral-500">Searching...</div>
                    ) : userResults.length === 0 ? (
                      <div className="px-3 py-2 text-xs text-neutral-500">No users found</div>
                    ) : (
                      userResults.map((u) => (
                        <button
                          type="button"
                          key={u.user_id}
                          onMouseDown={() => handlePickUser(u)}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-neutral-50 flex flex-col"
                        >
                          <span className="text-neutral-900">{u.email || u.user_id}</span>
                          {u.display_name && (
                            <span className="text-xs text-neutral-500">{u.display_name}</span>
                          )}
                        </button>
                      ))
                    )}
                  </div>
                )}
              </>
            )}
          </div>
          <div>
            <label
              htmlFor="user-workspace-org-select"
              className="block text-sm font-medium text-neutral-700 mb-1"
            >
              Client workspace
            </label>
            <CustomSelect
              aria-label="Client workspace"
              value={workspaceId}
              onChange={setWorkspaceId}
              options={orgOptions}
              placeholder="Select workspace..."
              searchable
            />
          </div>
          <div>
            <label
              htmlFor="user-workspace-role-select"
              className="block text-sm font-medium text-neutral-700 mb-1"
            >
              Role
            </label>
            <div className="flex gap-2">
              <CustomSelect
                aria-label="Role"
                value={role}
                onChange={(v) => setRole(v as 'owner' | 'member')}
                options={ROLE_OPTIONS}
              />
              <Button
                variant="primary"
                size="md"
                onClick={handleAdd}
                disabled={isSaving || !selectedUser || !workspaceId}
                aria-label="Assign the selected user to the selected workspace"
              >
                <Plus className="w-4 h-4" aria-hidden />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Mappings table */}
      <section
        id="user-workspace-mappings"
        className="bg-white border border-neutral-200 rounded-lg shadow-card overflow-hidden"
      >
        <div className="px-5 py-4 border-b border-neutral-200 flex flex-wrap items-center gap-3">
          <h2 className="text-13 font-semibold text-neutral-900 mr-auto">Current mappings</h2>
          <input
            type="text"
            value={filterUser}
            onChange={(e) => setFilterUser(e.target.value)}
            placeholder="Filter by user..."
            aria-label="Filter mappings by user"
            className="h-9 px-3 border border-neutral-200 rounded-md text-sm w-48 focus:outline-none focus:ring-2 focus:ring-primary-600 focus:ring-opacity-10"
          />
          <div className="w-56">
            <CustomSelect
              aria-label="Filter mappings by workspace"
              value={filterWorkspaceId}
              onChange={setFilterWorkspaceId}
              options={filterWorkspaceOptions}
              placeholder="All workspaces"
              searchable
            />
          </div>
        </div>
        {mappings.length === 0 ? (
          <div className="px-5 py-12 text-center">
            <UserIcon className="w-8 h-8 text-neutral-300 mx-auto mb-3" aria-hidden />
            <p className="text-sm text-neutral-500">No users have been mapped by an admin yet.</p>
          </div>
        ) : filteredMappings.length === 0 ? (
          <div className="px-5 py-12 text-center">
            <UserIcon className="w-8 h-8 text-neutral-300 mx-auto mb-3" aria-hidden />
            <p className="text-sm text-neutral-500">No mappings match this filter.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-neutral-50 text-left text-xs text-neutral-500">
                <th className="px-5 py-2.5 font-medium">User</th>
                <th className="px-5 py-2.5 font-medium">Workspace</th>
                <th className="px-5 py-2.5 font-medium">Role</th>
                <th className="px-5 py-2.5 font-medium">Mapped</th>
                <th className="px-5 py-2.5" />
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {filteredMappings.map((m) => (
                <tr key={`${m.workspace_id}:${m.user_id}`} className="hover:bg-neutral-50 transition">
                  <td className="px-5 py-3 text-neutral-900">
                    {m.user_email || m.user_name || m.user_id}
                  </td>
                  <td className="px-5 py-3 text-neutral-700">
                    {orgNameById.get(m.workspace_id) || m.workspace_id}
                  </td>
                  <td className="px-5 py-3 text-neutral-700 capitalize">{m.role}</td>
                  <td className="px-5 py-3 text-neutral-500">
                    {new Date(m.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-5 py-3 text-right">
                    <button
                      onClick={() => setPendingDelete(m)}
                      aria-label={`Remove ${m.user_email || m.user_id} from ${
                        orgNameById.get(m.workspace_id) || 'this workspace'
                      }`}
                      className="p-1.5 rounded text-neutral-400 hover:text-error-600 hover:bg-error-50 transition focus:outline-none focus:ring-2 focus:ring-error-600 focus:ring-opacity-20"
                    >
                      <Trash2 className="w-4 h-4" aria-hidden />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <ConfirmationModal
        isOpen={pendingDelete !== null}
        title="Remove this mapping?"
        message={`${
          pendingDelete?.user_email || pendingDelete?.user_id || 'This user'
        } will be re-homed to a fresh personal workspace.`}
        confirmText="Remove"
        variant="danger"
        onConfirm={handleDelete}
        onClose={() => setPendingDelete(null)}
      />
    </div>
  );
};

export default UserWorkspaceMappingTab;
